import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import classNames from "../../functions/classNames";
import { useDictionarySearch } from "../hooks/useVocabQueries";

const SEARCH_FIELDS = [
  { value: "any", label: "All" },
  { value: "lex", label: "Arabic" },
  { value: "phono", label: "CAPHI" },
  { value: "gloss", label: "Gloss" },
  { value: "group", label: "Group" },
];

const groupKeyFor = (entry) => (entry.group_id ? entry.group_id : `entry:${entry.id}`);

export const groupDictionaryEntries = (entries) => {
  const groups = new Map();
  entries.forEach((entry) => {
    const key = groupKeyFor(entry);
    if (!groups.has(key)) {
      groups.set(key, { key, groupId: entry.group_id || "", entries: [] });
    }
    groups.get(key).entries.push(entry);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    lemma:
      group.entries.find((entry) => (entry.entry_type || "").toUpperCase() === "LEMMA") ||
      group.entries[0],
  }));
};

function EntryTypeBadge({ type }) {
  if (!type) return null;
  const isLemma = type.toUpperCase() === "LEMMA";
  return (
    <span
      className={classNames(
        "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
        isLemma
          ? "bg-ramsalab-teal-blue/15 text-ramsalab-teal-blue"
          : "bg-gray-100 text-gray-600 dark:bg-ramsalab-light-navy dark:text-gray-300"
      )}
    >
      {type}
    </span>
  );
}

function VariantRow({ entry, isSelected, onSelect, nested }) {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(entry)}
      className={classNames(
        "w-full rounded-md border px-3 py-2 text-left transition",
        isSelected
          ? "border-ramsalab-teal-blue bg-ramsalab-teal-blue/10"
          : "border-transparent hover:bg-gray-50 dark:hover:bg-ramsalab-light-navy"
      )}
    >
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white" dir="auto">
          {entry.entry || entry.lex || entry.non_coda}
        </p>
        <EntryTypeBadge type={entry.is_temporary ? "temp" : entry.entry_type} />
      </div>
      {entry.phono && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{entry.phono}</p>
      )}
      {!nested && entry.english_gloss && (
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{entry.english_gloss}</p>
      )}
      <p className="mt-0.5 text-[11px] text-gray-400">
        {entry.pos}
        {entry.row_id ? ` · ${entry.row_id}` : ""}
      </p>
    </button>
  );
}

function GroupCard({ group, selectedEntryIds, onSelectEntry, defaultExpanded }) {
  const hasSelected = group.entries.some((entry) => selectedEntryIds.includes(entry.id));
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || hasSelected);
  const { lemma, entries, groupId } = group;

  useEffect(() => {
    if (hasSelected) setIsExpanded(true);
  }, [hasSelected]);

  if (!groupId) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-ramsalab-light-navy">
        <VariantRow
          entry={lemma}
          isSelected={selectedEntryIds.includes(lemma.id)}
          onSelect={onSelectEntry}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "rounded-lg border",
        hasSelected
          ? "border-ramsalab-teal-blue"
          : "border-gray-200 dark:border-ramsalab-light-navy"
      )}
    >
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full items-start justify-between gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-ramsalab-navy rounded-lg"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white" dir="auto">
            {lemma.lex || lemma.entry}
            {lemma.pos && (
              <span className="ml-2 text-xs font-normal text-gray-500">{lemma.pos}</span>
            )}
          </p>
          {lemma.english_gloss && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{lemma.english_gloss}</p>
          )}
          <p className="mt-1 text-[11px] text-gray-400">
            {groupId} · {entries.length} variant{entries.length === 1 ? "" : "s"}
            {lemma.root ? ` · root ${lemma.root}` : ""}
          </p>
        </div>
        <ChevronDownIcon
          className={classNames(
            "mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {isExpanded && (
        <div className="space-y-1 border-t border-gray-100 dark:border-ramsalab-light-navy px-2 py-2">
          {entries.map((entry) => (
            <VariantRow
              key={entry.id}
              entry={entry}
              nested
              isSelected={selectedEntryIds.includes(entry.id)}
              onSelect={onSelectEntry}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DictionarySearchPanel({
  className,
  searchQuery,
  onSearchQueryChange,
  onSelectEntry,
  selectedEntryIds = [],
  jobId,
  groupLimit = 20,
}) {
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [field, setField] = useState("any");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchQueryResult = useDictionarySearch({
    queryConfig: {
      queryKey: [debouncedQuery, field, jobId, groupLimit],
      apiParams: {
        query: debouncedQuery,
        field,
        limit: groupLimit,
        jobId,
        expandGroups: true,
      },
      enabled: debouncedQuery.trim().length >= 1,
    },
  });

  const groups = useMemo(
    () => groupDictionaryEntries(searchQueryResult.data || []),
    [searchQueryResult.data]
  );

  return (
    <div className={classNames("flex flex-col h-full min-h-0", className)}>
      <div className="relative shrink-0">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400"
        />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search lexicon (Arabic, CAPHI, gloss, LPG id)"
          className="w-full rounded-lg border border-gray-300 dark:border-ramsalab-light-navy bg-white dark:bg-ramsalab-navy py-2 pl-10 pr-3 text-sm text-gray-900 dark:text-white"
        />
      </div>
      <div className="mt-2 flex shrink-0 flex-wrap gap-1">
        {SEARCH_FIELDS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setField(option.value)}
            className={classNames(
              "rounded-full px-2.5 py-0.5 text-xs",
              field === option.value
                ? "bg-ramsalab-teal-blue text-white"
                : "bg-gray-100 text-gray-600 dark:bg-ramsalab-light-navy dark:text-gray-300"
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex-1 min-h-0 overflow-y-auto space-y-2">
        {searchQueryResult.isFetching && (
          <p className="text-sm text-gray-500">Searching…</p>
        )}
        {!searchQueryResult.isFetching && debouncedQuery && groups.length === 0 && (
          <p className="text-sm text-gray-500">No matches found.</p>
        )}
        {groups.map((group) => (
          <GroupCard
            key={group.key}
            group={group}
            selectedEntryIds={selectedEntryIds}
            onSelectEntry={onSelectEntry}
            defaultExpanded={groups.length === 1}
          />
        ))}
      </div>
    </div>
  );
}
